'use client';

import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import type { ComplianceJurisdiction } from '@selenos/leads';
import {
  ConsentCheckbox,
  getConsentText,
} from '@selenos/leads/components/consent-checkbox';
import { PhoneInput } from '@selenos/leads/components/phone-input';
import { useForm } from 'react-hook-form';

import { Alert, AlertDescription } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';

import { useExperiment } from '~/lib/analytics/experiment-context';
import { useTrackingParams } from '~/lib/tracking/use-tracking-params';

import {
  ChatbotLeadSchema,
  type ChatbotLeadFormValues,
} from './chatbot-lead-schema';

interface ChatbotLeadOverlayProps {
  verticalSlug: string;
  citySlug: string;
  jurisdiction: ComplianceJurisdiction;
  verticalName: string;
  submitAction: (data: Record<string, unknown>) => Promise<{
    success: boolean;
    leadId?: string;
    error?: string;
  }>;
  onLeadCaptured: (leadId?: string) => void;
}

export function ChatbotLeadOverlay({
  verticalSlug,
  citySlug,
  jurisdiction,
  verticalName,
  submitAction,
  onLeadCaptured,
}: ChatbotLeadOverlayProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const experiment = useExperiment();

  // UTM + click-ids + _fbp/_fbc cookies + experiment variant (shared hook).
  const tracking = useTrackingParams(experiment?.variant);

  const form = useForm<ChatbotLeadFormValues>({
    resolver: zodResolver(ChatbotLeadSchema),
    defaultValues: {
      phone: '',
      firstName: '',
      lastName: '',
      email: '',
      consentGiven: false as unknown as true,
    },
    mode: 'onTouched',
  });

  const onSubmit = async (data: ChatbotLeadFormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const consentText = getConsentText(jurisdiction, verticalName);

      const result = await submitAction({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        phone: data.phone,
        consentGiven: data.consentGiven,
        consentText,
        customFields: {},
        verticalSlug,
        citySlug,
        channel: 'chatbot',
        ...tracking,
      });

      if (result.success) {
        onLeadCaptured(result.leadId);
      } else {
        setSubmitError(
          result.error || 'Something went wrong. Please try again.',
        );
      }
    } catch {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-[#111] p-6">
        <h3 className="mb-1 text-lg font-semibold text-white">
          Enter your details to continue
        </h3>
        <p className="mb-5 text-sm text-white/50">
          We need your contact info to continue the conversation.
        </p>

        {submitError ? (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/70">Phone Number</FormLabel>
                  <FormControl>
                    <PhoneInput
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      required
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/70">First Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="First name"
                        data-test="chatbot-lead-firstName"
                        className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-white/70">Last Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Last name"
                        data-test="chatbot-lead-lastName"
                        className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white/70">
                    Email (optional)
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      data-test="chatbot-lead-email"
                      className="border-white/10 bg-white/5 text-white placeholder:text-white/30"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="consentGiven"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ConsentCheckbox
                      jurisdiction={jurisdiction}
                      checked={field.value === true}
                      onCheckedChange={field.onChange}
                      verticalName={verticalName}
                      error={
                        form.formState.errors.consentGiven?.message as
                          | string
                          | undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              data-test="chatbot-lead-submit"
              className="w-full"
              style={{ backgroundColor: 'var(--landing-primary)' }}
            >
              {isSubmitting ? 'Submitting...' : 'Continue Chatting'}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}
